import React from 'react';
import { Chip, Typography } from '@mui/material';
import { GridColDef, GridRowModel, GridRowModesModel } from '@mui/x-data-grid';
import { FGrid } from '../grid';
import { ISimulationAssumptionRow } from './simulation.models';

type Props = {
  rows: ISimulationAssumptionRow[];
  loading?: boolean;
  editable?: boolean;
  onChange: (rows: ISimulationAssumptionRow[]) => void;
  onSave: (rows: ISimulationAssumptionRow[]) => Promise<void>;
};
type State = { rowModesModel: GridRowModesModel; saving: boolean; error: string };

class SimulationAssumptionsGrid extends React.Component<Props, State> {
  state: State = { rowModesModel: {}, saving: false, error: '' };
  getRowId = (r: ISimulationAssumptionRow) => r.id || r.rid || r.rix || r.code;

  private validate = (row: ISimulationAssumptionRow): string => {
    if (row.required && (row.value === null || row.value === undefined || Number.isNaN(Number(row.value)))) {
      return 'A value is required.';
    }
    if (row.value !== null && row.value !== undefined) {
      const value = Number(row.value);
      if (!Number.isFinite(value)) return 'Value must be numeric.';
      if (row.minvalue !== null && row.minvalue !== undefined && value < Number(row.minvalue)) return `Minimum is ${row.minvalue}.`;
      if (row.maxvalue !== null && row.maxvalue !== undefined && value > Number(row.maxvalue)) return `Maximum is ${row.maxvalue}.`;
    }
    if (row.confidence !== null && row.confidence !== undefined) {
      const confidence = Number(row.confidence);
      if (confidence < 0 || confidence > 100) return 'Confidence must be between 0 and 100.';
    }
    return '';
  };

  columns: GridColDef[] = [
    { field: 'category', headerName: 'Category', width: 150 },
    { field: 'code', headerName: 'Code', width: 145 },
    { field: 'name', headerName: 'Assumption', flex: 1, minWidth: 250 },
    {
      field: 'sourceclass',
      headerName: 'Source',
      width: 175,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value === 'PUBLISHED_FORECAST' ? 'Published Forecast' : (p.value || 'Scenario Input')}
          color={p.value === 'PUBLISHED_FORECAST' ? 'info' : 'default'}
          variant={p.value === 'PUBLISHED_FORECAST' ? 'outlined' : 'filled'}
        />
      ),
    },
    { field: 'sourcechannel', headerName: 'Source Channel', width: 155 },
    { field: 'value', headerName: 'Value', width: 140, type: 'number', editable: true },
    { field: 'unit', headerName: 'Unit', width: 100 },
    { field: 'minvalue', headerName: 'Min', width: 110, type: 'number' },
    { field: 'maxvalue', headerName: 'Max', width: 110, type: 'number' },
    { field: 'confidence', headerName: 'Confidence %', width: 135, type: 'number', editable: true },
    { field: 'required', headerName: 'Required', width: 110, renderCell: (p) => <Chip size="small" label={p.value ? 'Yes' : 'No'} color={p.value ? 'success' : 'default'} /> },
    { field: 'status', headerName: 'Status', width: 125, renderCell: (p) => <Chip size="small" label={p.row.__error ? 'INVALID' : (p.value || '-')} color={p.row.__error ? 'error' : p.value === 'READY' ? 'success' : 'warning'} /> },
    { field: '__error', headerName: 'Validation', minWidth: 220, flex: 1, renderCell: (p) => <Typography variant="caption" color={p.value ? 'error' : 'text.secondary'}>{p.value || 'Valid'}</Typography> },
  ];

  processRowUpdate = (newRow: GridRowModel) => {
    const typed = newRow as ISimulationAssumptionRow;
    const error = this.validate(typed);
    const id = this.getRowId(typed);
    const rows = (this.props.rows || []).map((r) => String(this.getRowId(r)) === String(id)
      ? { ...r, ...typed, __dirty: true, __error: error }
      : r);
    this.props.onChange(rows);
    return { ...typed, __dirty: true, __error: error };
  };

  saveAll = async () => {
    const dirty = (this.props.rows || []).filter((r) => r.__dirty);
    const invalid = dirty.filter((r) => this.validate(r));
    if (!dirty.length) { this.setState({ error: 'There are no changed assumptions to save.' }); return; }
    if (invalid.length) { this.setState({ error: `Resolve ${invalid.length} invalid assumption row(s) before saving.` }); return; }
    this.setState({ saving: true, error: '' });
    try { await this.props.onSave(dirty); }
    finally { this.setState({ saving: false }); }
  };

  render() {
    return (
      <FGrid
        title="Scenario Assumptions"
        subtitle={this.state.error || 'Published Forecast values are inherited baseline inputs. Changes here apply only to this Simulation scenario.'}
        rows={this.props.rows || []}
        columns={this.columns}
        loading={this.props.loading || this.state.saving}
        height={560}
        showQuickFilter
        getRowId={this.getRowId}
        rowModesModel={this.state.rowModesModel}
        onRowModesModelChange={(rowModesModel: GridRowModesModel) => this.setState({ rowModesModel })}
        processRowUpdate={this.processRowUpdate}
        onProcessRowUpdateError={(error: any) => this.setState({ error: error?.message || String(error) })}
        saveAll={this.props.editable === false ? undefined : this.saveAll}
        isCellEditable={() => this.props.editable !== false}
        pageSizeOptions={[10, 25, 50]}
        getRowClassName={(p) => p.row.__error ? 'error-row' : p.row.__dirty ? 'dirty-row' : ''}
        sx={{ '& .error-row': { bgcolor: 'error.lighter' }, '& .dirty-row': { bgcolor: 'warning.lighter' } }}
      />
    );
  }
}
export default SimulationAssumptionsGrid;
